import { FieldName } from '@/interfaces/field-name.interface'
import { Filter } from '@/interfaces/filter.interface'
import { APIReport } from '@/models/api/report.model'
import { APIRole } from '@/models/api/role.model'
import { ReportsServices } from '@/services/modules/reports.service'
import store from '@/store'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'

@Module
export default class ReportsStore extends VuexModule {
    reports: object[] = []
    reportsPaginate: object[] = []
    reportFavoritesPaginate: object[] = []
    reportForm: object|null = null

    @Mutation
    updateReports (newReports: object[]) {
        this.reports = newReports
    }

    @Mutation
    updateReportsPaginate (newReports: object[]) {
        this.reportsPaginate = newReports
    }

    @Mutation
    updateReportFavoritesPaginate (newReportFavorites: object[]) {
        this.reportFavoritesPaginate = newReportFavorites
    }

    @Mutation
    updateReport (newReports: object) {
        this.reportForm = newReports
    }

    @Action
    getReports () {
        return new Promise((resolve) => {
            ReportsServices.getReports()
                .then(resp => {
                    store.commit('updateReports', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getReportsPaginate (params: { page: number; filters?: Filter[]; limit: number; sortBy: string; sort: string }) {
        return new Promise((resolve) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newParams: {[key: string]: any} = { page: params.page, limit: params.limit, sortBy: params.sortBy, sort: params.sort }

            if (params.filters) {
                params.filters.forEach((filter: Filter) => {
                    if (filter.queryValue) {
                        if (!newParams[filter.value]) {
                            newParams[filter.value] = filter.queryValue
                        } else {
                            let helper: string[] = []

                            if (Array.isArray(newParams[filter.value])) {
                                helper = [ ...newParams[filter.value] ]
                            } else {
                                helper.push(newParams[filter.value])
                            }

                            if (typeof filter.queryValue === 'string') helper.push(filter.queryValue)

                            newParams[filter.value] = helper
                        }
                    }
                })
            }
            ReportsServices.getReportsPaginate(newParams)
                .then(resp => {
                    store.commit('updateReportsPaginate', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getReportsById (id: number) {
        return new Promise((resolve) => {
            ReportsServices.getReportsById(id)
                .then(resp => {
                    store.commit('updateReport', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getReportShares (id: number) {
        return new Promise((resolve) => {
            ReportsServices.getReportShares(id)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    createReportShares (params: { reportId: number; users: number[] }) {
        return new Promise((resolve) => {
            ReportsServices.createReportShares(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    editReports (params: APIReport) {
        params = new APIReport(params)

        return new Promise((resolve) => {
            if (!(params instanceof APIReport)) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }
            ReportsServices.editReports(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    createReports (params: APIReport) {
        params = new APIReport(params)

        return new Promise((resolve) => {
            if (!(params instanceof APIReport)) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }
            ReportsServices.createReports(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    cloneReports (id: number) {
        return new Promise((resolve) => {
            ReportsServices.cloneReports(id)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    deleteReports (id: number) {
        return new Promise((resolve) => {
            ReportsServices.deleteReports(id)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    addRoleToReport ({ reportId, roles }: { reportId: number; roles: APIRole[] }) {
        return new Promise((resolve) => {
            const roleList: number[] = []

            roles.forEach(role => {
                if (role.roleId) roleList.push(role.roleId)
            })

            ReportsServices.addRoleToReport(reportId, roleList)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    removeRoleToReport ({ reportId, roles }: { reportId: number; roles: APIRole[] }) {
        return new Promise((resolve) => {
            const roleList: number[] = []

            roles.forEach(role => {
                if (role.roleId) roleList.push(role.roleId)
            })

            ReportsServices.removeRoleToReport(reportId, roleList)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getReportFilters (reportId: number) {
        return new Promise((resolve) => {
            ReportsServices.getReportFilters(reportId)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    viewReport ({ reportId, reportFilters }: { reportId: number; reportFilters: FieldName[] }) {
        return new Promise((resolve) => {
            return ReportsServices.viewReport(reportId, reportFilters)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    exportReportCsv ({ reportId, reportFilters }: { reportId: number; reportFilters: FieldName[] }) {
        return new Promise((resolve) => {
            ReportsServices.exportReportCsv(reportId, reportFilters)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getReportFavoritesPaginate (params: { page: number; filters?: Filter[]; limit: number }) {
        return new Promise((resolve) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newParams: {[key: string]: any} = { page: params.page, limit: params.limit }

            if (params.filters) {
                params.filters.forEach((filter: Filter) => {
                    if (filter.queryValue) {
                        if (!newParams[filter.value]) {
                            newParams[filter.value] = filter.queryValue
                        } else {
                            let helper: string[] = []

                            if (Array.isArray(newParams[filter.value])) {
                                helper = [ ...newParams[filter.value] ]
                            } else {
                                helper.push(newParams[filter.value])
                            }

                            if (typeof filter.queryValue === 'string') helper.push(filter.queryValue)

                            newParams[filter.value] = helper
                        }
                    }
                })
            }
            ReportsServices.getReportFavoritesPaginate(newParams)
                .then(resp => {
                    store.commit('updateReportFavoritesPaginate', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    createReportFavorites (params: {reportId: number; userId: number}) {
        return new Promise((resolve) => {
            ReportsServices.addFavorite(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    createReportUserFilter (params: {reportId: number; userId: number; createReportUserFilter: string; reportUserFilterData: string}) {
        return new Promise((resolve) => {
            ReportsServices.addUserFilter(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getReportUserFilter (params: {reportId: number; userId: number}) {
        return new Promise((resolve) => {
            ReportsServices.getUserFilter(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    deleteFavorites (id: number) {
        return new Promise((resolve) => {
            ReportsServices.deleteFavorites(id)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    deleteUserFilter (id: number) {
        return new Promise((resolve) => {
            ReportsServices.deleteUserFilter(id)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }
}
